import type { Exchange, Expiry } from '../../proto/generated/common'
import { exchangeToJSON } from '../../proto/generated/common'
import type { Security } from '../../proto/generated/security'

/**
 * Compact definition of one option series (underlying + expiry). Individual option
 * securities (<prefix><strike>_CE / _PE) are synthesised from this on demand instead of
 * being materialised up front, which is what used to put ~10^5 objects in memory.
 */
export interface OptionSeriesDef {
    /** `${exchange}:${securityCodePrefix}` — what an option key resolves to. */
    id: string
    exchange: Exchange
    underlyingCode: string
    securityCodePrefix: string
    symbolPrefix: string
    displayNamePrefix: string
    priceMultiplier?: number
    lotSize?: number
    volumeFreezeQuantity?: number
    tickSize?: number
    expiry?: Expiry
    name?: string
    strikes: number[]
}

/** Everything the main thread keeps in memory: cash + derived + futures, plus option series. */
export interface SecuritiesSnapshot {
    securities: Security[]
    optionSeries: OptionSeriesDef[]
}

export interface SecuritiesMeta {
    etag: string | null
    lastModified: string | null
    savedAt: number
    securityCount: number
    optionSeriesCount: number
}

export type SecuritiesSyncSource = 'idb' | 'network'

/** Millisecond durations of the phases inside the worker, for the console summary. */
export type SyncTimings = Record<string, number>

export interface StoredSecurities {
    snapshot: SecuritiesSnapshot | null
    /** ETag, else Last-Modified, of the stored snapshot; null when nothing is stored. */
    validator: string | null
    timings: SyncTimings
}

export interface RevalidateRequest {
    baseUrl: string
    token: string | null
}

export type RevalidateOutcome = (
    | { result: 'not-modified' }
    | { result: 'updated'; validator: string | null }
    | { result: 'failed'; message: string }
) & { timings: SyncTimings }

/** Main thread → worker. */
export type SecuritiesWorkerRequest =
    | { type: 'load' }
    | ({ type: 'revalidate' } & RevalidateRequest)

/** Worker → main thread. */
export type SecuritiesWorkerMessage =
    | { type: 'stored'; stored: StoredSecurities; postedAt: number }
    | { type: 'snapshot'; snapshot: SecuritiesSnapshot; validator: string | null; postedAt: number }
    | { type: 'revalidated'; outcome: RevalidateOutcome; postedAt: number }
    | { type: 'error'; message: string }

/** Same format as util/security.ts#getSecurityKey, duplicated here so the worker does not pull in the router. */
export const securityKey = (exchange: Exchange, code: string) => `${exchangeToJSON(exchange)}:${code}`

export const optionSeriesId = (exchange: Exchange, securityCodePrefix: string) =>
    `${exchangeToJSON(exchange)}:${securityCodePrefix}`
